import { lazy, Suspense, useEffect, useState } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider } from './presentation-layer/themes'

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { NSLoader } from './presentation-layer/components/atoms/NSLoader'
import { supabase } from './lib/supabase'
import { findStaffSessionStr, verifyStaffSessionJwt } from './services/auth.service'
import { isStaffSessionExpired } from './utils/sessionTimeout'
import { ServiceDownListener } from './components/ServiceDownListener'

// Lazy load heavy components
const SurveyApp = lazy(() => import('./ns_apps/survey/App').then(m => ({ default: m.SurveyApp })))

// Website / marketing pages
const WebsiteLogin = lazy(() => import('./pages/website/WebsiteLogin').then(m => ({ default: m.WebsiteLogin })))

/**
 * If user already has a session, redirect from `/` to their dashboard.
 * Otherwise show the login page as the default landing experience.
 */
function AuthAwareLanding() {
  const [redirect, setRedirect] = useState<string | null>(null)
  const [checked, setChecked] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        // Check for staff session first (admin OTP login — no Supabase session)
        const staffRaw = findStaffSessionStr()
        if (staffRaw && !cancelled) {
          const verifiedStaff = await verifyStaffSessionJwt(staffRaw)
          if (verifiedStaff) {
            const sessionTs = Number(verifiedStaff.ts) || 0
            if (!isStaffSessionExpired(sessionTs)) {
              setRedirect('/admin/dashboard')
              return
            }
          }
        }

        const { data: { session } } = await supabase.auth.getSession()
        if (!session || cancelled) { if (!cancelled) setChecked(true); return }
        const { data: profile } = await supabase
          .from('profiles')
          .select('role, tenant_id, tenant:tenants(client_type)')
          .eq('id', session.user.id)
          .maybeSingle()
        if (cancelled) return
        const role = profile?.role?.toLowerCase()
        if (role === 'superadmin' || role === 'super_admin') {
          setRedirect('/admin/dashboard')
        } else {
          const t = Array.isArray(profile?.tenant) ? profile.tenant[0] : profile?.tenant
          if ((t as any)?.client_type === 'Parent Client') {
            setRedirect('/control/dashboard')
          } else {
            setRedirect('/tenant/dashboard')
          }
        }
      } catch { /* ignore */ }
      if (!cancelled) setChecked(true)
    })()
    return () => { cancelled = true }
  }, [])

  if (redirect) return <Navigate to={redirect} replace />
  if (!checked) return <NSLoader isLoading={true} fullScreen={true} />
  return <WebsiteLogin />
}

function App() {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ThemeProvider>
        <Router>
          <ServiceDownListener />
          <Suspense fallback={<NSLoader isLoading={true} fullScreen={true} />}>
            <Routes>
              {/* ── Auth entry ── */}
              <Route path="/" element={<AuthAwareLanding />} />
              <Route path="/login" element={<WebsiteLogin />} />

              {/* Legacy orphan URLs → login */}
              <Route path="/gallery" element={<Navigate to="/" replace />} />
              <Route path="/survey" element={<Navigate to="/" replace />} />
              <Route path="/not-found" element={<Navigate to="/" replace />} />

              <Route path="/*" element={<SurveyApp />} />
            </Routes>
          </Suspense>
        </Router>
      </ThemeProvider>
    </LocalizationProvider>
  )
}

export default App

